import { useId, useState, type FormEvent } from "react";
import {
  ArrowRight,
  Users,
  Target,
  Layout,
  Network,
  Compass,
  LayoutGrid,
  List,
  type LucideIcon,
} from "lucide-react";
import type { AnalysisContext, ViewMode } from "../types";
import { useI18n } from "../i18n/I18nProvider";
import type { TranslationKey } from "../i18n/translations";

interface ContextFormProps {
  onSubmit: (data: AnalysisContext) => void;
}

const VIEW_OPTIONS: {
  id: ViewMode;
  labelKey: TranslationKey;
  descKey: TranslationKey;
  icon: LucideIcon;
}[] = [
  { id: "graph", labelKey: "views.graph", descKey: "views.graph.desc", icon: Network },
  {
    id: "compass",
    labelKey: "views.compass",
    descKey: "views.compass.desc",
    icon: Compass,
  },
  {
    id: "topics",
    labelKey: "views.topics",
    descKey: "views.topics.desc",
    icon: LayoutGrid,
  },
  { id: "table", labelKey: "views.table", descKey: "views.table.desc", icon: List },
];

export default function ContextForm({ onSubmit }: ContextFormProps) {
  const { t } = useI18n();
  const speakersId = useId();
  const goalsId = useId();
  const [speakers, setSpeakers] = useState("");
  const [goals, setGoals] = useState("");
  const [requestedViews, setRequestedViews] = useState<ViewMode[]>([
    "graph",
    "compass",
    "topics",
    "table",
  ]);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (speakers && goals && requestedViews.length > 0) {
      onSubmit({ speakers, goals, requestedViews });
    }
  };

  const toggleView = (view: ViewMode) => {
    setRequestedViews((prev) =>
      prev.includes(view) ? prev.filter((v) => v !== view) : [...prev, view],
    );
  };

  return (
    <div className="w-full max-w-2xl">
      <header className="mb-12">
        <h2 className="text-ink mb-4 text-4xl font-light tracking-tight">
          {t("context.heading")}
        </h2>
        <p className="text-zinc-700">{t("context.subheading")}</p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-10" noValidate>
        <div className="space-y-4">
          <label
            htmlFor={speakersId}
            className="flex items-center gap-2 text-sm font-medium tracking-widest text-zinc-700 uppercase"
          >
            <Users className="h-4 w-4" aria-hidden="true" />
            {t("context.speakers")}
          </label>
          <textarea
            id={speakersId}
            value={speakers}
            onChange={(e) => setSpeakers(e.target.value)}
            placeholder={t("context.speakers.placeholder")}
            className="glass text-ink min-h-[100px] w-full resize-none rounded-3xl border-white/40 p-6 text-lg transition-all focus:border-emerald-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30"
            required
          />
        </div>

        <div className="space-y-4">
          <label
            htmlFor={goalsId}
            className="flex items-center gap-2 text-sm font-medium tracking-widest text-zinc-700 uppercase"
          >
            <Target className="h-4 w-4" aria-hidden="true" />
            {t("context.goals")}
          </label>
          <textarea
            id={goalsId}
            value={goals}
            onChange={(e) => setGoals(e.target.value)}
            placeholder={t("context.goals.placeholder")}
            className="glass text-ink min-h-[100px] w-full resize-none rounded-3xl border-white/40 p-6 text-lg transition-all focus:border-emerald-500/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/30"
            required
          />
        </div>

        <fieldset className="space-y-4">
          <legend className="flex items-center gap-2 text-sm font-medium tracking-widest text-zinc-700 uppercase">
            <Layout className="h-4 w-4" aria-hidden="true" />
            {t("context.views")}
          </legend>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {VIEW_OPTIONS.map((option) => {
              const Icon = option.icon;
              const active = requestedViews.includes(option.id);
              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => toggleView(option.id)}
                  aria-pressed={active}
                  className={`flex flex-col gap-3 rounded-[32px] border-2 p-6 text-left transition-all focus-visible:ring-4 focus-visible:ring-emerald-500/30 focus-visible:outline-none ${
                    active
                      ? "border-emerald-500 bg-emerald-50/50 ring-4 ring-emerald-500/10"
                      : "glass border-white/40 hover:border-zinc-300"
                  }`}
                >
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-2xl ${
                      active ? "bg-emerald-600 text-white" : "bg-zinc-100 text-zinc-500"
                    }`}
                  >
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="serif text-ink mb-1 text-xl font-light tracking-tight italic">
                      {t(option.labelKey)}
                    </div>
                    <div className="text-xs leading-relaxed text-zinc-700">
                      {t(option.descKey)}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </fieldset>

        <button
          type="submit"
          disabled={requestedViews.length === 0}
          className="bg-ink disabled:hover:bg-ink flex w-full items-center justify-center gap-2 rounded-full py-5 text-lg font-medium text-white transition-all hover:bg-emerald-600 focus-visible:ring-4 focus-visible:ring-emerald-500/30 focus-visible:outline-none active:scale-[0.98] disabled:opacity-50"
        >
          {t("context.submit")}
          <ArrowRight className="h-5 w-5" aria-hidden="true" />
        </button>
      </form>
    </div>
  );
}
